import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUserGroupListQuery } from '@/hook';
import { AddTask, VButton, VFlex, VStack, useFlag } from '@/package';

import { MemberAllocateRatioTable } from '../MemberTask/MemberAllocateRatioTable';
import { TaskClassConfidence } from './TaskClassConfidence';
import { TaskCreateFormUser } from './TaskCreateFormUser';
import { TaskDistsance } from './TaskDistsance';
import { TaskDueDate } from './TaskDueDate';
import { TaskFrameInterval } from './TaskFrameInterval';
import { TaskName } from './TaskName';
import { TaskPath } from './TaskPath';
import { TaskWorker } from './TaskWorker';
import { useFormDefaultValue, useTaskCreateForm } from './hook';
import type { TaskCreateFormProps } from './type';

export const TaskCreateForm = ({
  defaultValue,
  readOnlyField,
  renderFooter,
  onCancel,
  onTaskCreated,
  projectData,
}: TaskCreateFormProps) => {
  const isEditing = !!defaultValue;
  const { t } = useTranslation();
  const formDefaultValue = useFormDefaultValue({
    defaultValue,
    project: projectData,
  });
  const { methods, handleCreateTask } = useTaskCreateForm({
    formDefaultValue,
    onTaskCreated,
  });
  const {
    formState: { isValid },
  } = methods;
  const {
    value: isUserAdding,
    setFalse: setIsUserAddingFalse,
    setTrue: setIsUserAddingTrue,
  } = useFlag(false);

  const readOnlyClassConfidence = !!readOnlyField?.classConfidence;
  const readOnlyDate = !!readOnlyField?.date;
  const readOnlyDistance = !!readOnlyField?.distance;
  const readOnlyTaskName = !!readOnlyField?.taskName;
  const readOnlyWorker = !!readOnlyField?.worker;
  const { data: userGroups } = useUserGroupListQuery();
  const userList = projectData?.user ?? [];
  const extendClassList =
    (isEditing
      ? defaultValue?.extendClass
      : projectData?.extendClass?.filter(item => item.enable)) ?? [];

  return (
    <FormProvider {...methods}>
      <VStack gap={2} className="overflow-auto">
        <TaskPath readOnly={Boolean(readOnlyField?.taskFolder)} />

        <VFlex gap={1} className="w-full">
          <VStack gap={1} className="flex-1">
            <div role="heading" className="text-body-xl font-semibold">
              {t('task.taskSetting.hedgebote')}
            </div>

            <TaskName readOnly={readOnlyTaskName} isEditing={isEditing} />
            <TaskDueDate readOnly={readOnlyDate} />
            <TaskFrameInterval readOnly={false} />

            {!isEditing ? (
              <MemberAllocateRatioTable
                readOnly={readOnlyWorker}
                onAddUser={() => setIsUserAddingTrue()}
              />
            ) : (
              <TaskWorker
                projectUserList={userList}
                readOnly={readOnlyWorker}
                userGroups={userGroups ?? []}
              />
            )}
          </VStack>

          <VStack gap={1} className="flex-1">
            <VStack gap={0.5}>
              <div role="heading" className="text-body-xl font-semibold">
                <span className="relative after:absolute after:top-[2px] after:-right-2 after:size-1 after:rounded-full after:bg-status-failed after:content-['']">
                  {t('task.aiAnnotationSettings.proverbialize')}
                </span>
              </div>

              <div className="pl-5 text-body-md text-info-500">
                <div className="list-item list-disc">
                  {t('task.aiAnnotationSettingsDescription0.absurd')}
                </div>
                <div className="list-item list-disc">
                  {t('task.aiAnnotationSettingsDescription1.absurdity')}
                </div>
                ;
              </div>
            </VStack>

            <TaskDistsance readonly={readOnlyDistance} />

            <TaskClassConfidence
              readOnly={readOnlyClassConfidence}
              extendClassList={extendClassList}
            />
          </VStack>
        </VFlex>

        {renderFooter ? (
          renderFooter(methods)
        ) : (
          <VFlex items="center" className="w-full pb-10" gap={1}>
            <VButton
              fluid
              outlined
              size="lg"
              variant="secondary"
              onClick={() => onCancel?.()}
            >
              {t('task.cancelCreateAction.nonduality')}
            </VButton>
            <VButton
              disabled={!isValid}
              fluid
              size="lg"
              type="submit"
              onClick={() => handleCreateTask()}
            >
              <AddTask className="size-5" />
              {t('task.createTaskButtonText.reiteration')}
            </VButton>
          </VFlex>
        )}

        {isUserAdding && (
          <TaskCreateFormUser
            isUserAdding={isUserAdding}
            setIsUserAdding={v =>
              v ? setIsUserAddingTrue() : setIsUserAddingFalse()
            }
            userList={userList}
            userGroups={userGroups}
          />
        )}
      </VStack>
    </FormProvider>
  );
};
